// @ts-nocheck
import React, { useState, useRef } from "react";
import { useQueryClient } from "react-query";
import moment from "moment";
import { t } from "i18next";
import formatDate from "includes/formatDate";
import useUnread from "classes/Accounts/hooks/useUnread";
import TagLabel, { NewLabel } from "components/Tags/Label/TagLabel";
import useAccount from "classes/Accounts/hooks/useAccount";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt } from "@fortawesome/free-regular-svg-icons";
import DocsTargetsCell from "./Cells/Targets";
import DocsTableToolbar from "./DocsTableToolbar";
import InfosFetcher from "components/InfosFetcher/InfosFetcher";
import genRequest from "includes/request";
import Table from "components/Table/Table";

export default function DocsTable(props) {
	const { isAdmin } = useAccount();
	const { setAsReaded } = useUnread();
	const QCL = useQueryClient();
	const [selected, setSelected] = useState([]);
	const state_ref = useRef();

	React.useEffect(() => {
		if (props.state !== state_ref.current && selected?.length)
			setSelected([]);
		state_ref.current = props.state;
	}, [selected, props, setSelected]);

	const cols = [
		{
			title: t("Table.TITLE"),
			field: "name",
			classes: "post-title-cell",
			render: (data) => <TitleCell item={data} />,
		},
		{
			title: t("Posts.VIEWS"),
			field: "viewCount",
			classes: "post-views-cell",
			noValuePh: "0",
			render: (data) => <ViewsCell item={data} />,
			hidden: !isAdmin(),
		},
		{
			title: t("Posts.PUBLISHED_DATE"),
			field: "publishedDate",
			classes: "post-date-cell",
			render: (data) => <DateCell item={data} />,
		},
		{
			title: t("Commons.CATEGORIES"),
			field: "Categories",
			classes: "post-cats-cell",
			render: (data) => <CategoriesCell item={data} />,
			sorting: false,
		},
		{
			title: t("Commons.TARGETS"),
			field: "Targets",
			render: (data) => <DocsTargetsCell item={data} />,
			sorting: false,
			hidden: !isAdmin(),
		},
	];

	// useless for now
	const filterItems = async (data) => {
		const catArray = props.dispCats;
		if (catArray.length === 0) return data;
		const filteredData = await Promise.all(data.map(async (a) => {
			const test = await genRequest("categories/document/" + ((typeof a.documentId === 'function') ? a.documentId() : (a.documentId || a.id))).catch(() => { });
			// same rule as the tiles (DocumentModel): the doc has every selected category
			return (Array.isArray(test) && test.length && catArray.every((id) => test.some((c) => c.id === id))) ? a : null;
		}));

		return filteredData.filter(item => item !== null);
	}

	function incrementViews(doc) {
		let req = genRequest("documents/" + doc.id + "/increment-view", null, "put")
			.then((resp) => {
				return resp;
			})
			.catch(() => { });
		return req;
	}

	function handleClick(e, item) {
		incrementViews(item).then(() => {
			setAsReaded("NotViewedDocUtils", item.id);
			QCL.invalidateQueries(["NotViewed", "Me", "NotViewedDocUtils"]);
		});
		props.handleDocClick(item);
	}

	let items = props.displayed;

	if (!items || !items.length) items = [];

	return (
		<div
			className={
				"DocsTable w-100" + (props.curDoc?.id ? " has-cur" : "")
			}
		>
			<Table
				onRowClick={handleClick}
				columns={cols}
				filter={filterItems}
				data={items}
				rowIdKey={"id"}
				activeRowId={props.curDoc?.id}
				options={{
					selection: isAdmin(),
				}}
				overloads={{
					Toolbar: (cprops, options) => (
						<DocsTableToolbar {...cprops} {...options} />
					),
				}}
			/>
		</div>
	);
}

const TitleCell = React.memo(({ item }) => {
	const { isReaded } = useUnread();
	let isDocReaded = isReaded("NotViewedDocUtils", item.id);
	let title = item.name;

	return (
		<div className="title-cell">
			{!isDocReaded && <NewLabel className="me-1" />}
			{title}
		</div>
	);
});
TitleCell.displayName = "TitleCell";

const ViewsCell = React.memo(({ item }) => {
	return (
		<InfosFetcher
			queryFn={() =>
				genRequest(`documents/${item.id}/views`)
			}
			queryKey={["documents", item.id, "views"]}
			treat={(infos) => {
				item.viewCount = infos?.length;
				return infos?.length ? infos.length : 0;
			}}
			displayOnError={true}
			options={{
				retry: 0,
			}}
		/>
	);
});
ViewsCell.displayName = "ViewsCell";

const DateCell = React.memo(({ item }) => {
	let date = formatDate();

	return (
		<div className="d-flex align-items-center flex-wrap gap-1 justify-content-between">
			<span title={moment(item.publishedDate).format("D MMM YYYY HH:mm")}>
				{moment(item.publishedDate).format(t("Events.TILE_DATE_FORMAT"))}
			</span>
			{item.publishedDate >= date && (
				<div className="d-flex flex-wrap gap-1 align-items-center justify-content-center me-1 date-pictos">
					{item.publishedDate >= date && (
						<div
							title={`${t("Commons.DEFERRED_TO")} ${formatDate(
								item.publishedDate,
								t("Commons.CUSTOM_FORMAT_DATE")
							)}`}
						>
							<FontAwesomeIcon
								className="differed-picto"
								title={t("Commons.DEFERRED_PUBLISH")}
								icon={faCalendarAlt}
							/>
						</div>
					)}
				</div>
			)}
		</div>
	);
});
DateCell.displayName = "DateCell";

const CategoriesCell = React.memo(({ item }) => {
	return (
		<InfosFetcher
			queryKey={["Categories", "Document", (typeof item.documentId === 'function') ? item.documentId() : (item.documentId || item.id)]}
			queryFn={() => genRequest("categories/document/" + ((typeof item.documentId === 'function') ? item.documentId() : (item.documentId || item.id)))}
			treat={(resp) => {
				if (!resp?.length) return false;
				return (
					<div className="d-flex flex-wrap gap-1">
						{resp
							.sort((a, b) => (a.categoryName < b.categoryName ? -1 : 1))
							.map((a) => (
								<TagLabel
									key={a.id}
									text={a.categoryName}
									color={a.categoryColor}
								/>
							))}
					</div>
				);
			}}
			options={{
				retry: 0,
			}}
		/>
	);
});
CategoriesCell.displayName = "CategoriesCell";
